/**
 * Analyzes screen definitions to extract navigation connections
 */

export interface NavigationConnection {
    id: string;
    fromScreenId: string;
    toScreenId: string;
    fromElementId?: string;
    action: string;
    label?: string;
    trigger: 'button' | 'automatic' | 'payment' | 'timeout';
}

export function analyzeNavigationConnections(
    screens: ScreenDefinition[],
): NavigationConnection[] {
    const connections: NavigationConnection[] = [];

    console.log(
        'Analyzing navigation connections for screens:',
        screens.map((s) => s.id),
    );

    screens.forEach((screen) => {
        // Check for automatic navigation after timeout
        if (screen.navigateDestination && screen.navigateAfterTimeout) {
            connections.push({
                id: `${screen.id}-timeout-${screen.navigateDestination}`,
                fromScreenId: screen.id,
                toScreenId: screen.navigateDestination,
                action: 'timeout',
                label: `${screen.navigateAfterTimeout}s`,
                trigger: 'timeout',
            });
        }

        // Check for automatic reset after timeout
        if (screen.resetAfterTimeout) {
            // Find the first screen to reset to
            const firstScreen = screens.find((s, index) => index === 0);
            if (firstScreen && firstScreen.id !== screen.id) {
                connections.push({
                    id: `${screen.id}-reset-${firstScreen.id}`,
                    fromScreenId: screen.id,
                    toScreenId: firstScreen.id,
                    action: 'reset',
                    label: `reset ${screen.resetAfterTimeout}s`,
                    trigger: 'timeout',
                });
            }
        }

        // Check for capture stage navigation
        if (screen.stage.type === 'capture') {
            const metadata = screen.stage.metadata as any;
            console.log(`Capture screen ${screen.id} metadata:`, metadata);
            if (
                metadata.shouldNavigateAfterCapture &&
                metadata.postCaptureDestination
            ) {
                console.log(
                    `Adding capture connection: ${screen.id} -> ${metadata.postCaptureDestination}`,
                );
                connections.push({
                    id: `${screen.id}-capture-${metadata.postCaptureDestination}`,
                    fromScreenId: screen.id,
                    toScreenId: metadata.postCaptureDestination,
                    action: 'capture',
                    label: 'after capture',
                    trigger: 'automatic',
                });
            }
        }

        // Check for processing stage navigation
        if (screen.stage.type === 'processing') {
            const metadata = screen.stage.metadata as any;
            console.log(`Processing screen ${screen.id} metadata:`, metadata);
            if (
                metadata.shouldNavigateAfterProcessing &&
                metadata.postProcessingDestination
            ) {
                console.log(
                    `Adding processing connection: ${screen.id} -> ${metadata.postProcessingDestination}`,
                );
                connections.push({
                    id: `${screen.id}-processing-${metadata.postProcessingDestination}`,
                    fromScreenId: screen.id,
                    toScreenId: metadata.postProcessingDestination,
                    action: 'processing',
                    label: 'after processing',
                    trigger: 'automatic',
                });
            }
        }

        // Check button actions in elements
        // Ensure elements array exists, default to empty array if undefined
        const elements = screen.elements || [];
        elements.forEach((element) => {
            if (element.type === 'button') {
                const buttonMetadata = element.metadata as any;
                console.log(`Button ${element.id} on screen ${screen.id}:`, {
                    action: buttonMetadata.action,
                    destination: buttonMetadata.destination,
                    hasAction: buttonMetadata.action !== undefined,
                    hasDestination: buttonMetadata.destination !== undefined,
                    metadata: buttonMetadata,
                });

                const actions = Array.isArray(buttonMetadata.action)
                    ? buttonMetadata.action
                    : [buttonMetadata.action];

                actions.forEach((action: string) => {
                    if (action === 'navigate' && buttonMetadata.destination) {
                        console.log(
                            `Adding button connection: ${screen.id} -> ${buttonMetadata.destination}`,
                        );
                        connections.push({
                            id: `${screen.id}-${element.id}-${buttonMetadata.destination}`,
                            fromScreenId: screen.id,
                            toScreenId: buttonMetadata.destination,
                            fromElementId: element.id,
                            action: 'navigate',
                            label: buttonMetadata.text || 'Navigate',
                            trigger: 'button',
                        });
                    }
                    // Skip reset, share, and related actions - these are exit points
                    // and should not show navigation connections
                });
            } else if (element.type === 'paymentQR') {
                const qrMetadata = element.metadata as any;
                if (qrMetadata.destination) {
                    connections.push({
                        id: `${screen.id}-${element.id}-payment-${qrMetadata.destination}`,
                        fromScreenId: screen.id,
                        toScreenId: qrMetadata.destination,
                        fromElementId: element.id,
                        action: 'payment',
                        label: 'Payment Success',
                        trigger: 'payment',
                    });
                }
            }
        });
    });

    console.log('Total connections found:', connections.length, connections);
    return connections;
}

export function getConnectionColor(
    trigger: NavigationConnection['trigger'],
): string {
    switch (trigger) {
        case 'button':
            return '#3B82F6'; // blue
        case 'automatic':
            return '#10B981'; // green
        case 'payment':
            return '#F59E0B'; // amber
        case 'timeout':
            return '#EF4444'; // red
        default:
            return '#6B7280'; // gray
    }
}

export function getConnectionStyle(trigger: NavigationConnection['trigger']): {
    strokeWidth: number;
    dash?: number[];
} {
    switch (trigger) {
        case 'button':
            return { strokeWidth: 2 };
        case 'automatic':
            return { strokeWidth: 3 };
        case 'payment':
            return { strokeWidth: 2, dash: [5, 5] };
        case 'timeout':
            return { strokeWidth: 2, dash: [10, 5] };
        default:
            return { strokeWidth: 1 };
    }
}
